// ============================================================================
// App Zoom Controller
// ============================================================================
// Reproduces browser-style zoom inside the Pake desktop shell, whose webview
// has no browser chrome / menu to provide zoom of its own (and works the same
// in a plain browser tab).
//
// Zoom is applied as CSS `zoom` on <html>, so the whole document reflows just
// like native browser zoom - layout, modals, and toasts all scale. Two places
// CSS `zoom` differs from native zoom, and both have one fix each:
//
// 1. CSS viewport units (vh/vw/dvh) still resolve against the REAL viewport
//    and then get multiplied by the factor - a raw 100vh box paints
//    zoom-times the screen height, pushing bottom bars off-screen. main.css
//    defines compensation tokens (--vh/--vw/--dvh, --viewport-h/-w/-dvh =
//    unit divided by --app-zoom, which _apply() mirrors below). RULE: size
//    viewport-bound boxes with those tokens, or with inset pairs on
//    position:fixed elements (the fixed containing block already shrinks
//    with zoom) - never raw vh/vw/dvh in a declaration. This makes the
//    layout viewport shrink and the page re-lay out, exactly like Chrome.
//
// 2. JS-positioned floating overlays (assignee/status dropdowns, grid menus,
//    popovers, tooltips): they are appended to <body> and therefore
//    re-scaled by the root zoom, so their pixel positions must be computed
//    in LAYOUT space rather than visual space. Positioners read `getZoom()`
//    and convert viewport measurements through `toLayoutPx()` /
//    `rectToLayout()`.
//
// At zoom 1 every conversion is a no-op, so unzoomed behaviour is
// byte-identical to before.
// ============================================================================

import { getPref, setPref } from 'shared/utils/user-prefs.js';

const STORAGE_KEY = 'appZoom';

// Browser-like discrete zoom levels. 1 = 100%.
const STEPS = [0.5, 0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2];
const MIN = STEPS[0];
const MAX = STEPS[STEPS.length - 1];

// Accumulated wheel delta (px-equivalent) needed before stepping one level.
// Tuned so a single mouse-wheel notch is ~one step while trackpad pinch
// (many tiny deltas) still feels smooth.
const WHEEL_STEP_THRESHOLD = 100;

// The single source of truth for the current zoom factor. Positioners read it
// via getZoom(); the controller mutates it.
let factor = 1;

class ZoomController {
    constructor() {
        this.indicator = null;
        this.indicatorTimer = null;
        this._wheelAccum = 0;
        this.onKeyDown = this._onKeyDown.bind(this);
        this.onWheel = this._onWheel.bind(this);
    }

    /** Apply the persisted zoom and start listening for zoom gestures. */
    init() {
        const saved = Number(getPref(STORAGE_KEY, 1));
        factor = this._snap(Number.isFinite(saved) ? saved : 1);
        this._apply(false);

        window.addEventListener('keydown', this.onKeyDown);
        // passive:false so ctrl+wheel can be intercepted (stop native scroll).
        window.addEventListener('wheel', this.onWheel, { passive: false });
    }

    getFactor() { return factor; }

    /** Set an absolute factor (snapped to the nearest discrete level). */
    setFactor(value, showIndicator = true) {
        const next = this._snap(value);
        if (next === factor) {
            if (showIndicator) this._showIndicator();
            return;
        }
        factor = next;
        this._apply(showIndicator);
    }

    zoomIn() { this.setFactor(this._adjacent(1)); }
    zoomOut() { this.setFactor(this._adjacent(-1)); }
    reset() { this.setFactor(1); }

    cleanup() {
        window.removeEventListener('keydown', this.onKeyDown);
        window.removeEventListener('wheel', this.onWheel);
        clearTimeout(this.indicatorTimer);
        this.indicator?.remove();
        this.indicator = null;
    }

    // ------------------------------------------------------------------ //

    _apply(showIndicator) {
        const root = document.documentElement;
        // Empty string clears the inline property at 100% so the default flow
        // is untouched (and no stray `zoom:1` lingers in the DOM).
        root.style.zoom = factor === 1 ? '' : String(factor);
        root.style.setProperty('--app-zoom', String(factor));
        setPref(STORAGE_KEY, factor);
        if (showIndicator) this._showIndicator();
    }

    _clamp(v) { return Math.min(MAX, Math.max(MIN, v)); }

    /** Snap an arbitrary factor to the nearest discrete step. */
    _snap(v) {
        const c = this._clamp(Number.isFinite(v) ? v : 1);
        let best = STEPS[0];
        let bestDist = Infinity;
        for (const s of STEPS) {
            const d = Math.abs(s - c);
            if (d < bestDist) { bestDist = d; best = s; }
        }
        return best;
    }

    /** Neighbouring level in the given direction (+1 up, -1 down). */
    _adjacent(dir) {
        const idx = STEPS.indexOf(this._snap(factor));
        const nextIdx = Math.min(STEPS.length - 1, Math.max(0, idx + dir));
        return STEPS[nextIdx];
    }

    _onKeyDown(e) {
        // Only the plain zoom chords - ignore when Alt is held so app shortcuts
        // that use Ctrl+Alt are never hijacked.
        if (!(e.ctrlKey || e.metaKey) || e.altKey) return;

        // e.key already folds the numpad variants: NumpadAdd -> '+',
        // NumpadSubtract -> '-', Numpad0 -> '0'.
        const key = e.key;
        if (key === '=' || key === '+') {
            e.preventDefault();
            this.zoomIn();
        } else if (key === '-') {
            e.preventDefault();
            this.zoomOut();
        } else if (key === '0') {
            e.preventDefault();
            this.reset();
        }
    }

    _onWheel(e) {
        if (!(e.ctrlKey || e.metaKey)) return;
        // Stop the native page zoom / scroll so we own the gesture entirely.
        e.preventDefault();

        // Normalise line / page deltas to a pixel-equivalent.
        let delta = e.deltaY;
        if (e.deltaMode === 1) delta *= 16;        // lines
        else if (e.deltaMode === 2) delta *= 100;  // pages

        // Scrolling up (negative deltaY) zooms in.
        this._wheelAccum -= delta;

        while (this._wheelAccum >= WHEEL_STEP_THRESHOLD) {
            this._wheelAccum -= WHEEL_STEP_THRESHOLD;
            this.zoomIn();
        }
        while (this._wheelAccum <= -WHEEL_STEP_THRESHOLD) {
            this._wheelAccum += WHEEL_STEP_THRESHOLD;
            this.zoomOut();
        }
    }

    _showIndicator() {
        if (!this.indicator) {
            this.indicator = document.createElement('div');
            this.indicator.className = 'zoom-indicator';
            this.indicator.setAttribute('aria-hidden', 'true');
            document.body.appendChild(this.indicator);
        }
        this.indicator.textContent = `${Math.round(factor * 100)}%`;
        this.indicator.classList.add('zoom-indicator--visible');
        clearTimeout(this.indicatorTimer);
        this.indicatorTimer = setTimeout(() => {
            this.indicator?.classList.remove('zoom-indicator--visible');
        }, 1100);
    }
}

export const zoom = new ZoomController();

/** Current zoom factor (1 = 100%). */
export function getZoom() {
    return factor;
}

/**
 * Convert a visual-pixel measurement (getBoundingClientRect / innerWidth) into
 * the LAYOUT-pixel space that a body-appended, root-zoomed overlay is
 * positioned in. No-op at zoom 1.
 * @param {number} px
 * @returns {number}
 */
export function toLayoutPx(px) {
    return factor === 1 ? px : px / factor;
}

/**
 * Convert a visual-space DOMRect into layout space. Returns a plain object with
 * the same fields (left/top/right/bottom/width/height). No-op at zoom 1.
 * @param {DOMRect} rect
 */
export function rectToLayout(rect) {
    if (factor === 1) return rect;
    const f = factor;
    return {
        left: rect.left / f,
        top: rect.top / f,
        right: rect.right / f,
        bottom: rect.bottom / f,
        width: rect.width / f,
        height: rect.height / f,
        x: rect.x / f,
        y: rect.y / f
    };
}
